import autocompleteField from '../../../vendor/lacodix/resourcerer/resources/js/components/autocompleteField'

/**
 * Domain-neutral person-or-free-text autocomplete.
 *
 * It supports an imperative method mode used by roster cells and a deferred
 * property mode used by ordinary forms. The input remains a filter; only a
 * clicked/keyboard-confirmed option or an exact Enter match becomes a person.
 * Every fuzzy or otherwise unconfirmed value remains explicit free text.
 */
export default ({
  method = null,
  args = [],
  exclude = [],
  optionsSelector = '[data-person-or-free-text-options]',
  personProperty = null,
  nameProperty = null,
  errorTitle = 'Error',
  errorMessage = 'Save failed'
}) => {
  const base = autocompleteField('', {})
  let wireId = null
  let positioningTransition = null
  let positioningTransitionHandler = null

  return {
    ...base,
    method,
    args,
    optionsSelector,
    personProperty,
    nameProperty,
    errorTitle,
    errorMessage,
    exclude: Array.isArray(exclude) ? exclude.map(String) : Object.values(exclude ?? {}).map(String),
    pending: [],
    navigated: false,
    selectedLabel: null,
    _options: null,
    _pendingSeq: 0,
    _baseInit: base.init,
    _baseOpenDropdown: base.openDropdown,
    _baseCloseDropdown: base.closeDropdown,
    _baseForceClose: base._forceClose,
    _baseDestroy: base.destroy,

    get selectionMode() {
      return typeof this.personProperty === 'string' && this.personProperty !== '' &&
        typeof this.nameProperty === 'string' && this.nameProperty !== ''
    },

    _livewire() {
      return wireId && window.Livewire ? (window.Livewire.find(wireId) || null) : null
    },

    init() {
      this._baseInit()
      const root = this.$root && this.$root.closest ? this.$root.closest('[wire\\:id]') : null
      wireId = root ? root.getAttribute('wire:id') : null

      this.$watch('value', (value) => {
        this.activeIndex = null
        this.navigated = false

        if (this.selectionMode && value !== this.selectedLabel) {
          this.selectedLabel = null
          this.syncSelection(null, value.trim() || null)
        }
      })
    },

    get elements() {
      if (this._options === null) {
        const holder = document.querySelector(this.optionsSelector)
        this._options = holder ? JSON.parse(holder.textContent || '{}') : {}
      }

      return this._options
    },

    get elementObjects() {
      return Object.entries(this.elements)
        .filter(([id]) => !this.exclude.includes(String(id)))
        .map(([id, name]) => ({ id: String(id), name }))
    },

    get filteredElements() {
      const term = this.value.trim().toLowerCase()

      return term
        ? this.elementObjects.filter((element) => element.name.toLowerCase().includes(term))
        : this.elementObjects
    },

    get sortedFilteredElements() {
      return [...this.filteredElements].sort((elementA, elementB) => elementA.name.localeCompare(elementB.name))
    },

    get exactMatch() {
      const text = this.value.trim().toLowerCase()

      return text
        ? (this.elementObjects.find((element) => element.name.toLowerCase() === text) ?? null)
        : null
    },

    openDropdown() {
      this._options = null
      this._baseOpenDropdown()
      this.activeIndex = null
      this._watchPositioningTransition()
    },

    closeDropdown() {
      this._clearPositioningTransition()
      this._baseCloseDropdown()
    },

    _forceClose() {
      this._clearPositioningTransition()
      this._baseForceClose()
    },

    _watchPositioningTransition() {
      this._clearPositioningTransition()

      const container = this.$refs.container
      const transition = container?.closest?.('.transform.transition-all') ?? null
      if (!transition) return

      positioningTransition = transition
      positioningTransitionHandler = (event) => {
        if (event.target !== positioningTransition) return

        this.updateDropdownPosition()
        this._clearPositioningTransition()
      }
      transition.addEventListener('transitionend', positioningTransitionHandler)
      transition.addEventListener('transitioncancel', positioningTransitionHandler)
    },

    _clearPositioningTransition() {
      if (positioningTransition && positioningTransitionHandler) {
        positioningTransition.removeEventListener('transitionend', positioningTransitionHandler)
        positioningTransition.removeEventListener('transitioncancel', positioningTransitionHandler)
      }

      positioningTransition = null
      positioningTransitionHandler = null
    },

    updateDropdownPosition() {
      if (!this.open) return

      const container = this.$refs.container
      if (!container || !container.isConnected) {
        this._forceClose()
        return
      }

      const inputRect = container.getBoundingClientRect()
      const panel = this.$refs.dropdown?.firstElementChild
      const dropdownHeight = panel?.offsetHeight || this.$refs.dropdown?.offsetHeight || 272
      const spaceBelow = window.innerHeight - inputRect.bottom
      this.dropUp = spaceBelow < dropdownHeight && inputRect.top > spaceBelow

      // This picker is always teleported to body and its panel is fixed.
      // Therefore coordinates must stay viewport-relative even when the input
      // lives inside a transformed/overflow-hidden modal.
      this.dropdownTop = this.dropUp
        ? inputRect.top - dropdownHeight
        : inputRect.bottom
      this.dropdownLeft = inputRect.left
      this.dropdownWidth = inputRect.width
    },

    moveActiveIndex(direction) {
      if (!this.open) return

      const maxIndex = this.sortedFilteredElements.length - 1
      if (maxIndex < 0) return

      if (this.activeIndex === null) {
        this.activeIndex = direction === 'down' ? 0 : maxIndex
      } else if (direction === 'up') {
        this.activeIndex = this.activeIndex <= 0 ? maxIndex : this.activeIndex - 1
      } else {
        this.activeIndex = this.activeIndex >= maxIndex ? 0 : this.activeIndex + 1
      }

      this.scrollToActiveElement()
    },

    handleKeyDown(event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        if (!this.open) this.openDropdown()
        this.navigated = true
        this.moveActiveIndex(event.key === 'ArrowDown' ? 'down' : 'up')
      } else if (event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()
        const active = this.navigated && this.activeIndex !== null
          ? this.sortedFilteredElements[this.activeIndex]
          : null
        active ? this.setElement(active.id) : this.submitTyped()
      } else if (event.key === 'Escape' || event.key === 'Tab') {
        this.closeDropdown()
      } else if (!this.open) {
        this.openDropdown()
      }
    },

    setElement(id) {
      this.submitValue(String(id))
    },

    submitTyped() {
      const text = this.value.trim()

      if (!text) {
        this.closeDropdown()
        return
      }

      this.submitValue(this.exactMatch ? this.exactMatch.id : text)
    },

    submitValue(value) {
      if (this.selectionMode) {
        this.selectValue(value)
        return
      }

      return this.callMethod(value)
    },

    selectValue(value) {
      const personName = Object.prototype.hasOwnProperty.call(this.elements, value)
        ? this.elements[value]
        : null
      const label = personName ?? String(value).trim()

      this.selectedLabel = label
      this.value = label
      this.closeDropdown()
      this.syncSelection(personName === null ? null : value, personName === null ? label : null)
    },

    syncSelection(personId, name) {
      const wire = this._livewire()
      if (!wire) return

      // Deferred updates make a later form submit carry the exact XOR state
      // without a request per keystroke. Resolve Livewire.find() for every use.
      wire.set(this.personProperty, personId, false)
      wire.set(this.nameProperty, name, false)
    },

    async callMethod(value) {
      const wire = this._livewire()
      const entry = { key: ++this._pendingSeq, label: this.elements[value] ?? value }
      this.pending.push(entry)
      this.value = ''
      this.closeDropdown()

      try {
        if (!wire || !this.method) throw new Error('personOrFreeTextPicker: Livewire method not resolvable')
        await wire[this.method](...this.args, value)
      } catch (error) {
        window.dispatchEvent(new CustomEvent('notify', {
          detail: { type: 'danger', title: this.errorTitle, message: this.errorMessage }
        }))
        throw error
      } finally {
        this.pending = this.pending.filter((pendingEntry) => pendingEntry.key !== entry.key)
      }
    },

    destroy() {
      this._clearPositioningTransition()
      this._baseDestroy()
    }
  }
}
